import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Cropper, { Area } from 'react-easy-crop';
import { AlertCircle, CheckCircle, Info, Trash2 } from 'lucide-react';

import { theme } from '../styles/theme';
import {
  iosNativeContent,
  iosNativeGridSingleCol,
  iosNativeHeaderStack,
  iosNativeInset,
  iosNativePageRoot,
  iosNativeScreenSubtitle,
  iosNativeScreenTitle,
  iosNativeTipsCallout,
} from '../styles/iosNativePageCss';
import { Button } from '../components/ui/Button';
import { PhotoDropzone } from '../components/upload/PhotoDropzone';
import { PhotoPreview } from '../components/upload/PhotoPreview';
import { SideSelector } from '../components/upload/SideSelector';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { useToast } from '../components/ui/Toast';
import { useSubscription } from '../hooks/useSubscription';
import { PrescriptionForm } from '../components/PrescriptionForm';
import { canPerformScan } from '../utils/scanLimits';
import {
  ProcessedPhoto,
  processPhoto,
  PhotoValidationError,
  PhotoMetadata,
} from '../utils/photoProcessing';
import { getCroppedImage } from '../utils/cropImage';
import {
  createScanId,
  deleteScanAssets,
  deleteScanRecord,
  generateScanAnalysis,
  listUserScans,
  saveScanRecord,
  ScanAsset,
  ScanRecord,
  updateScanRecord,
  uploadScanAssets,
} from '../services/scanService';
import { updateStreakOnScan } from '../utils/streakCalculator';
import { calculateGlowScore } from '../utils/glowScoreCalculator';
import { performPreScanQualityCheck, checkSameConditions } from '../utils/preScanQuality';
import { validateScanPhotos } from '../services/rekognitionService';
import { detectAcne } from '../services/acneDetectionService';
import type { ScanStatus } from '../services/scanService';

type Slot = 'front' | 'side';

interface SlotState {
  processed: ProcessedPhoto | null;
  warnings: string[];
  infos: string[];
  error: string;
  autoOpen: boolean;
  uploadProgress: number | null;
}

interface DuplicateHistoryItem {
  scanId: string;
  timestamp: string;
  frontUrl: string;
  sideUrl: string;
  frontThumbUrl: string;
  sideThumbUrl: string;
  hashes: {
    front: string;
    side: string;
  };
  status: ScanStatus;
  glowScore?: number;
}

const UploadPage = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2.5rem 1.5rem 4rem;
  display: flex;
  justify-content: center;

  @media (max-width: 1023px) {
    padding: 1rem 0.75rem 3rem;
  }

  ${iosNativePageRoot}
`;

const Content = styled.div`
  width: 100%;
  max-width: 1080px;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 1023px) {
    gap: 1rem;
  }

  ${iosNativeContent}
`;

const Header = styled.header`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  body.native-app & {
    text-align: left;
    align-items: stretch;
  }

  ${iosNativeHeaderStack}
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: clamp(2rem, 3vw, 2.75rem);
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};

  @media (max-width: 1023px) {
    font-size: 1.5rem;
  }

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.base};
  max-width: 640px;
  margin: 0 auto;
  line-height: 1.7;

  @media (max-width: 1023px) {
    font-size: ${theme.typography.fontSize.sm};
    line-height: 1.5;
  }

  body.native-app & {
    margin: 0;
  }

  ${iosNativeScreenSubtitle}
`;

const GuidanceCard = styled.section`
  background: white;
  border-radius: ${theme.borderRadius['3xl']};
  padding: 1.75rem;
  box-shadow: ${theme.shadows.base};
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
  align-items: start;

  @media (min-width: 480px) {
    grid-template-columns: 1.2fr 1fr;
    align-items: center;
  }

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1.5fr 1fr;
    padding: 1.75rem;
  }

  @media (max-width: 1023px) {
    padding: 1rem;
    gap: 0.75rem;
  }

  ${iosNativeInset}
`;

const GuidanceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.75rem;
`;

const GuidanceItem = styled.li`
  display: flex;
  gap: 0.65rem;
  align-items: flex-start;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
    color: ${theme.colors.primary[400]};
  }
`;

const TipsCard = styled.div`
  border-radius: ${theme.borderRadius['2xl']};
  background: ${theme.colors.primary[50]};
  padding: 1.5rem;
  color: ${theme.colors.primary[700]};
  font-size: ${theme.typography.fontSize.sm};
  box-shadow: inset 0 0 0 1px ${theme.colors.primary[100]};

  @media (max-width: 1023px) {
    padding: 0.75rem 1rem;
    font-size: ${theme.typography.fontSize.xs};

    ul {
      margin: 0.5rem 0 0 !important;
      padding-left: 1rem !important;
    }
  }

  ${iosNativeTipsCallout}
`;

const UploadGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 1023px) {
    gap: 0.75rem;
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }

  ${iosNativeGridSingleCol}
`;

const UploadCard = styled.article`
  background: rgba(255, 255, 255, 0.95);
  border-radius: ${theme.borderRadius['3xl']};
  padding: 1.75rem;
  box-shadow: ${theme.shadows.base};
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 1023px) {
    padding: 1rem;
    gap: 0.65rem;
  }

  ${iosNativeInset}
`;

const SlotHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const SlotTitle = styled.h2`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;

  @media (max-width: 1023px) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const SlotSubtitle = styled.p`
  margin: 0;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};

  @media (max-width: 1023px) {
    font-size: ${theme.typography.fontSize.xs};
  }
`;

const ActionsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: flex-end;
  align-items: center;
`;

const PrivacyNotice = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: ${theme.borderRadius['2xl']};
  padding: 1.25rem 1.5rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  svg {
    flex-shrink: 0;
    color: ${theme.colors.primary[400]};
  }

  @media (max-width: 1023px) {
    padding: 0.75rem 1rem;
    font-size: ${theme.typography.fontSize.xs};
  }

  ${iosNativeInset}
`;

const PrescriptionSection = styled.div`
  margin-bottom: 2rem;

  @media (max-width: 1023px) {
    margin-bottom: 1rem;
  }
`;

const ErrorBanner = styled.div`
  background: ${theme.colors.accent.peach}15;
  border: 1px solid ${theme.colors.accent.peach};
  border-radius: ${theme.borderRadius.xl};
  padding: 1rem 1.25rem;
  color: ${theme.colors.text.primary};
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;

  ${iosNativeInset}
`;

const SuccessBanner = styled.div`
  background: ${theme.colors.mint[100]};
  border: 1px solid ${theme.colors.mint[300]};
  border-radius: ${theme.borderRadius.xl};
  padding: 1.1rem 1.25rem;
  display: flex;
  gap: 0.75rem;
  align-items: center;
  color: ${theme.colors.mint[800]};

  ${iosNativeInset}
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  width: min(560px, 100%);
  background: white;
  border-radius: ${theme.borderRadius['2xl']};
  padding: 1.75rem;
  box-shadow: ${theme.shadows.lg};
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  ${iosNativeInset}
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.text.primary};
  font-weight: ${theme.typography.fontWeight.semibold};
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Snackbar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.text.primary};
  color: white;
  padding: 0.85rem 1.25rem;
  border-radius: ${theme.borderRadius.full};
  box-shadow: ${theme.shadows.md};
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 1300;
`;

const SnackbarButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.primary[200]};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  font-size: ${theme.typography.fontSize.sm};
`;

const ScanNameInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  background: white;
  border-radius: ${theme.borderRadius['2xl']};
  box-shadow: ${theme.shadows.base};

  ${iosNativeInset}
`;

const ScanNameLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const ScanNameField = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.primary};
  background: ${theme.colors.background.primary};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
`;

const CropContainer = styled.div`
  position: relative;
  width: 100%;
  height: clamp(320px, 60vh, 520px);
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius['2xl']};
  overflow: hidden;

  body.native-app & {
    border-radius: 12px;
    border: 0.5px solid rgba(60, 60, 67, 0.12);
  }
`;

const CropControls = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
`;

const Slider = styled.input`
  width: 180px;
`;

const DeleteSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-radius: ${theme.borderRadius['2xl']};
  background: white;
  box-shadow: ${theme.shadows.base};

  ${iosNativeInset}
`;

const DeleteCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  h4 {
    margin: 0;
    font-size: ${theme.typography.fontSize.lg};
    color: ${theme.colors.text.primary};
  }

  p {
    margin: 0;
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.text.secondary};
  }
`;

const initialSlotState: SlotState = {
  processed: null,
  warnings: [],
  infos: [],
  error: '',
  autoOpen: false,
  uploadProgress: null,
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value));
};

// Deterministic pseudo-random number generator based on image hash
// This ensures the same images always produce the same score
// calculateGlowScore has been moved to src/utils/glowScoreCalculator.ts
// This ensures centralized, deterministic scoring across the app

const useLocalHistory = (uid: string | null) => {
  const storageKey = uid ? `glowlens_scan_history_${uid}` : null;

  const load = useCallback((): DuplicateHistoryItem[] => {
    if (!storageKey) return [];
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      return JSON.parse(raw) as DuplicateHistoryItem[];
    } catch (error) {
      console.warn('Failed to parse scan history', error);
      return [];
    }
  }, [storageKey]);

  const save = useCallback(
    (item: DuplicateHistoryItem) => {
      if (!storageKey) return;
      const current = load().filter((entry) => entry.scanId !== item.scanId);
      current.unshift(item);
      const trimmed = current.slice(0, 10);
      localStorage.setItem(storageKey, JSON.stringify(trimmed));
    },
    [load, storageKey]
  );

  const findDuplicate = useCallback(
    (hashes: { front: string; side: string }): DuplicateHistoryItem | null => {
      if (!storageKey) return null;
      const entries = load();
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      // Only treat as duplicate if BOTH front AND side hashes match exactly
      // If either hash is different, it's a new scan
      for (const entry of entries) {
        if (
          entry.hashes.front === hashes.front &&
          entry.hashes.side === hashes.side &&
          new Date(entry.timestamp).getTime() >= cutoff
        ) {
          return entry;
        }
      }
      return null;
    },
    [load, storageKey]
  );

  const remove = useCallback(
    (scanId: string) => {
      if (!storageKey) return;
      const remaining = load().filter((entry) => entry.scanId !== scanId);
      localStorage.setItem(storageKey, JSON.stringify(remaining));
    },
    [load, storageKey]
  );

  return { save, load, findDuplicate, remove };
};

const ScanPage: React.FC = () => {
  const rememberLastScanMeta = (meta: { scanId: string; timestamp: string }) => {
    sessionStorage.setItem('glowlens_last_scan_meta', JSON.stringify(meta));
  };

  const navigate = useNavigate();
  const { userProfile, getCurrentSession, updateUserProfile } = useAWSAuth();
  const { showToast } = useToast();
  const { tier } = useSubscription();
  const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);
  const [limitReachedMessage, setLimitReachedMessage] = useState<string>('');
  const [scansUsed, setScansUsed] = useState<number | undefined>(undefined);
  const [scansAllowed, setScansAllowed] = useState<number | 'unlimited' | undefined>(undefined);

  const [slotState, setSlotState] = useState<Record<Slot, SlotState>>({
    front: { ...initialSlotState },
    side: { ...initialSlotState },
  });
  const [sideAngle, setSideAngle] = useState<'left' | 'right'>('right');
  
  // Store separate photos for left and right sides
  const [leftSidePhoto, setLeftSidePhoto] = useState<ProcessedPhoto | null>(null);
  const [rightSidePhoto, setRightSidePhoto] = useState<ProcessedPhoto | null>(null);
  
  // Custom scan name
  const [customScanName, setCustomScanName] = useState<string>('');
  
  // Reset all state when component mounts - ensure fresh upload every time
  useEffect(() => {
    console.log('[DEBUG] ScanPage mounted - resetting all state');
    setSlotState({
      front: { ...initialSlotState },
      side: { ...initialSlotState },
    });
    setSideAngle('right');
    setLeftSidePhoto(null);
    setRightSidePhoto(null);
    setCustomScanName(''); // Reset custom name
    setGlobalError('');
    setStatus('idle');
    setIsContinuing(false);
    setScanId(null);
    setScanRecord(null);
    setUploadedAssets(null);
    setDuplicateCandidate(null);
    setShowDuplicateModal(false);
    setForceNewScan(false);
    console.log('[DEBUG] State reset complete - all state cleared', {
      sidePhotoFile: null,
      sidePhotoUrl: null,
      sideAngle: 'right',
      leftSidePhoto: null,
      rightSidePhoto: null,
    });
  }, []); // Empty deps - only run on mount
  
  // Update side photo state when sideAngle changes to show the correct photo
  useEffect(() => {
    const photoForCurrentSide = sideAngle === 'left' ? leftSidePhoto : rightSidePhoto;
    if (photoForCurrentSide) {
      // Show the photo for the currently selected side
      setSlotState((prev) => ({
        ...prev,
        side: {
          processed: photoForCurrentSide,
          warnings: photoForCurrentSide.warnings,
          infos: mapWarningsToInfos(photoForCurrentSide),
          error: '',
          autoOpen: false,
          uploadProgress: prev.side.uploadProgress,
        },
      }));
      console.log('[DEBUG] Showing photo for current side', {
        sideAngle,
        hasPhoto: true,
        fileName: photoForCurrentSide.file.name,
      });
    } else {
      // No photo for this side, clear it
      setSlotState((prev) => ({
        ...prev,
        side: {
          ...initialSlotState,
          autoOpen: false,
        },
      }));
      console.log('[DEBUG] No photo for current side, showing dropzone', {
        sideAngle,
        hasLeftPhoto: !!leftSidePhoto,
        hasRightPhoto: !!rightSidePhoto,
      });
    }
  }, [sideAngle, leftSidePhoto, rightSidePhoto]);
  const [globalError, setGlobalError] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'uploading' | 'complete' | 'deleting' | 'error'>(
    'idle'
  );
  const [isContinuing, setIsContinuing] = useState(false);
  const [scanId, setScanId] = useState<string | null>(null);
  const [scanRecord, setScanRecord] = useState<ScanRecord | null>(null);
  const [uploadedAssets, setUploadedAssets] = useState<Record<'front' | 'side', ScanAsset> | null>(
    null
  );
  const [deleteTimer, setDeleteTimer] = useState<NodeJS.Timeout | null>(null);
  const [deleteCountdown, setDeleteCountdown] = useState<number>(0);
  const [showDeleteSnackbar, setShowDeleteSnackbar] = useState(false);

  const history = useLocalHistory(userProfile?.uid ?? null);

  const [duplicateCandidate, setDuplicateCandidate] = useState<DuplicateHistoryItem | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [forceNewScan, setForceNewScan] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [cropState, setCropState] = useState<{
    open: boolean;
    slot: Slot | null;
    imageSrc: string;
    rotation: number;
    zoom: number;
    crop: { x: number; y: number };
    croppedAreaPixels: Area | null;
  }>({
    open: false,
    slot: null,
    imageSrc: '',
    rotation: 0,
    zoom: 1,
    crop: { x: 0, y: 0 },
    croppedAreaPixels: null,
  });

  // Update side photo state when sideAngle changes to show the correct photo
  useEffect(() => {
    const photoForCurrentSide = sideAngle === 'left' ? leftSidePhoto : rightSidePhoto;
    if (photoForCurrentSide) {
      // Show the photo for the currently selected side
      setSlotState((prev) => ({
        ...prev,
        side: {
          processed: photoForCurrentSide,
          warnings: photoForCurrentSide.warnings,
          infos: mapWarningsToInfos(photoForCurrentSide),
          error: '',
          autoOpen: false,
          uploadProgress: prev.side.uploadProgress,
        },
      }));
    } else {
      // No photo for this side, clear it
      setSlotState((prev) => ({
        ...prev,
        side: {
          ...initialSlotState,
          autoOpen: false,
        },
      }));
    }
  }, [sideAngle, leftSidePhoto, rightSidePhoto]);

  // Require front photo and at least one side photo (left or right)
  const bothPhotosReady = useMemo(
    () => Boolean(slotState.front.processed && (leftSidePhoto || rightSidePhoto)),
    [slotState.front.processed, leftSidePhoto, rightSidePhoto]
  );
  
  // Check if all three photos are ready
  const allThreePhotosReady = useMemo(
    () => Boolean(slotState.front.processed && leftSidePhoto && rightSidePhoto),
    [slotState.front.processed, leftSidePhoto, rightSidePhoto]
  );

  const resetSlotError = (slot: Slot) => {
    setSlotState((prev) => ({
      ...prev,
      [slot]: {
        ...prev[slot],
        error: '',
      },
    }));
  };

  // Handle retake - clear all photos and reset state
  const handleRetake = () => {
    // Clear all photos
    setSlotState({
      front: {
        processed: null,
        warnings: [],
        infos: [],
        error: '',
        autoOpen: false,
        uploadProgress: null,
      },
      side: {
        processed: null,
        warnings: [],
        infos: [],
        error: '',
        autoOpen: false,
        uploadProgress: null,
      },
    });
    
    // Clear side photos
    setLeftSidePhoto(null);
    setRightSidePhoto(null);
    
    // Reset status and errors
    setStatus('idle');
    setIsContinuing(false);
    setGlobalError('');
    setScanId(null);
    
    // Show success message
    showToast('Photos cleared. You can now upload new photos.', 'success');
  };

  const mapWarningsToInfos = (processed: ProcessedPhoto): string[] => {
    const infos: string[] = [];
    if (processed.metadata.brightness && processed.metadata.brightness > 130) {
      infos.push('Lighting looks even — great for analysis.');
    }
    if ((processed.metadata.facesDetected ?? 0) === 1) {
      infos.push('Exactly one face detected.');
    }
    if (processed.metadata.sizeKB < 500) {
      infos.push('Photo compressed for faster upload while preserving detail.');
    }
    return infos;
  };

  const handleFileSelection = async (slot: Slot, file: File) => {
    console.log('[DEBUG] handleFileSelection called', { 
      slot, 
      fileName: file.name, 
      fileSize: file.size,
      fileType: file.type,
      currentSideAngle: slot === 'side' ? sideAngle : undefined,
      hasExistingProcessed: !!slotState[slot].processed,
    });
    
    resetSlotError(slot);
    setGlobalError('');
    setDuplicateCandidate(null);
    setShowDuplicateModal(false);
    setForceNewScan(false);
    setScanId(null); // Reset scanId when new images are selected
    
    // Clear the existing processed photo immediately to ensure UI updates
    setSlotState((prev) => ({
      ...prev,
      [slot]: {
        ...initialSlotState,
        error: '',
        autoOpen: false,
        uploadProgress: null,
      },
    }));

    try {
      const processed = await processPhoto(file);
      console.log('[DEBUG] Photo processed successfully', {
        slot,
        hash: processed.metadata.hash,
        width: processed.metadata.width,
        height: processed.metadata.height,
        sizeKB: processed.metadata.sizeKB,
        sideAngle: slot === 'side' ? sideAngle : undefined,
      });
      
      // For side photos, store in the appropriate side state (left or right)
      if (slot === 'side') {
        if (sideAngle === 'left') {
          setLeftSidePhoto(processed);
          console.log('[DEBUG] Left-side photo uploaded and stored', {
            fileName: file.name,
            hash: processed.metadata.hash,
            width: processed.metadata.width,
            height: processed.metadata.height,
          });
        } else {
          setRightSidePhoto(processed);
          console.log('[DEBUG] Right-side photo uploaded and stored', {
            fileName: file.name,
            hash: processed.metadata.hash,
            width: processed.metadata.width,
            height: processed.metadata.height,
          });
        }
        
        // Update slotState to show the uploaded photo
      setSlotState((prev) => ({
        ...prev,
        [slot]: {
          processed,
          warnings: processed.warnings,
          infos: mapWarningsToInfos(processed),
          error: '',
          autoOpen: false,
            uploadProgress: null,
        },
      }));
        
        showToast(`${sideAngle === 'left' ? 'Left' : 'Right'}-side photo uploaded successfully.`, 'success');
        if ((processed.metadata.facesDetected ?? 0) > 1) {
        showToast('We detected multiple faces. Try to ensure just your face is in frame.', 'info');
        }
      } else {
        // For front photo, update normally
        setSlotState((prev) => {
          const newState = {
            ...prev,
            [slot]: {
              processed,
              warnings: processed.warnings,
              infos: mapWarningsToInfos(processed),
              error: '',
              autoOpen: false,
              uploadProgress: null,
            },
          };
          console.log('[DEBUG] State updated for slot', {
            slot,
            hasProcessed: !!newState[slot].processed,
            dataUrl: newState[slot].processed?.dataUrl?.substring(0, 50) + '...',
            hash: newState[slot].processed?.metadata.hash,
          });
          return newState;
        });
      }
    } catch (error) {
      console.error('[DEBUG] Photo processing error', { slot, error, fileName: file.name });
      const message =
        error instanceof PhotoValidationError
          ? error.message
          : 'Unable to process that photo. Please try a different image.';
      setSlotState((prev) => ({
        ...prev,
        [slot]: {
          ...prev[slot],
          error: message,
          processed: null,
        },
      }));
    }
  };

  const handleReplace = (slot: Slot) => {
    console.log('[DEBUG] handleReplace called', { 
      slot,
      sideAngle: slot === 'side' ? sideAngle : undefined,
      hadProcessed: !!slotState[slot].processed,
    });
    
    if (slot === 'side') {
      // Clear the photo for the current side (left or right)
      if (sideAngle === 'left') {
        setLeftSidePhoto(null);
        console.log('[DEBUG] Cleared left-side photo');
      } else {
        setRightSidePhoto(null);
        console.log('[DEBUG] Cleared right-side photo');
      }
      // Clear slotState for side
    setSlotState((prev) => ({
      ...prev,
      [slot]: {
        ...initialSlotState,
        autoOpen: true,
      },
    }));
    } else {
      // For front photo, clear normally
      setSlotState((prev) => ({
        ...prev,
        [slot]: {
          ...initialSlotState,
          autoOpen: true,
        },
      }));
    }
    
    // Clear duplicate state when replacing
    setDuplicateCandidate(null);
    setShowDuplicateModal(false);
    setForceNewScan(false);
    setScanId(null);
  };

  const handleOpenCrop = (slot: Slot) => {
    const processed = slotState[slot].processed;
    if (!processed) return;
    setCropState({
      open: true,
      slot,
      imageSrc: processed.dataUrl,
      rotation: 0,
      zoom: 1,
      crop: { x: 0, y: 0 },
      croppedAreaPixels: null,
    });
  };

  const handleCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCropState((prev) => ({
      ...prev,
      croppedAreaPixels,
    }));
  }, []);

  const commitCrop = async () => {
    if (!cropState.slot || !cropState.croppedAreaPixels) {
      setCropState((prev) => ({ ...prev, open: false }));
      return;
    }
    try {
      const croppedFile = await getCroppedImage(
        cropState.imageSrc,
        cropState.croppedAreaPixels,
        cropState.rotation,
        `${cropState.slot}.jpg`
      );
      await handleFileSelection(cropState.slot, croppedFile);
      setCropState((prev) => ({ ...prev, open: false }));
      showToast(`${cropState.slot === 'front' ? 'Front' : 'Side'} photo cropped.`, 'success');
    } catch (error) {
      console.error('Crop error', error);
      showToast('We could not crop that photo. Please try again.', 'error');
    }
  };

  const hashPair = useMemo(() => {
    if (!slotState.front.processed) return null;
    const currentSidePhoto = sideAngle === 'left' ? leftSidePhoto : rightSidePhoto;
    if (!currentSidePhoto) return null;
    return {
      front: slotState.front.processed.metadata.hash,
      side: currentSidePhoto.metadata.hash,
    };
  }, [slotState.front.processed, sideAngle, leftSidePhoto, rightSidePhoto]);

  // DISABLED: Duplicate detection blocking - always allow new uploads
  const checkForDuplicate = () => {
    // Always return false to allow new uploads
    // User wants always-new behavior for uploads on /scan
    console.log('[DEBUG] Duplicate check disabled - allowing new upload');
    return false;
  };

  const dispatchScanReady = (id: string) => {
    window.dispatchEvent(
      new CustomEvent('glowlens:scanReady', {
        detail: { scanId: id },
      })
    );
  };

  const persistHistory = (record: ScanRecord) => {
    history.save({
      scanId: record.scanId,
      timestamp: record.createdAt,
      frontUrl: record.front.url,
      sideUrl: record.side.url,
      frontThumbUrl: record.front.thumbUrl,
      sideThumbUrl: record.side.thumbUrl,
      hashes: record.hashes,
      status: record.status,
      glowScore: record.glowScore,
    });
  };

  const handleContinue = async (skipDuplicateCheck?: boolean) => {
    setGlobalError('');
    if (!bothPhotosReady) {
      setGlobalError('Upload both the front and side photos before continuing.');
      return;
    }

    if (!userProfile) {
      setGlobalError('You need to be signed in to upload scans.');
      return;
    }

    // Check scan limits based on subscription tier
    const session = await getCurrentSession();
    if (!session) {
      setGlobalError('Your session expired. Please sign in again.');
      return;
    }

    const uid = session.getIdToken().payload.sub;
    const scanCheck = await canPerformScan(tier, session, uid);
    
    if (!scanCheck.allowed) {
      setLimitReachedMessage(scanCheck.reason || 'You have reached your weekly scan limit.');
      setScansUsed(scanCheck.scansUsed);
      setScansAllowed(scanCheck.scansAllowed);
      setShowLimitReachedModal(true);
      return;
    }

    // Skip duplicate check - always create new scan
    // Removed duplicate detection blocking as per requirements
    console.log('[DEBUG] Proceeding with new scan upload (duplicate check disabled)');

    setIsContinuing(true);
    setStatus('uploading');
    try {
      // Session already retrieved above
      const uid = session.getIdToken().payload.sub;
      // Always create a new scanId for new uploads
      const newScanId = !scanId ? createScanId() : scanId;
      setScanId(newScanId);
      
      // Verify we have front photo and at least one side photo
      if (!slotState.front.processed) {
        throw new Error('Front photo is required');
      }
      if (!leftSidePhoto && !rightSidePhoto) {
        throw new Error('At least one side photo (left or right) is required');
      }
      
      // Prepare upload assets for all available photos
      const uploadAssets: Array<{
        slot: 'front' | 'side';
        file: File;
        thumbnail: Blob;
        metadata: PhotoMetadata;
        angle?: 'left' | 'right';
      }> = [
          {
            slot: 'front',
            file: slotState.front.processed!.file,
            thumbnail: slotState.front.processed!.thumbnail.blob,
            metadata: slotState.front.processed!.metadata,
          },
      ];
      
      // Add left side photo if available
      if (leftSidePhoto) {
        uploadAssets.push({
            slot: 'side',
          file: leftSidePhoto.file,
          thumbnail: leftSidePhoto.thumbnail.blob,
          metadata: leftSidePhoto.metadata,
          angle: 'left',
        });
      }
      
      // Add right side photo if available
      if (rightSidePhoto) {
        uploadAssets.push({
          slot: 'side',
          file: rightSidePhoto.file,
          thumbnail: rightSidePhoto.thumbnail.blob,
          metadata: rightSidePhoto.metadata,
          angle: 'right',
        });
      }
      
      console.log('[DEBUG] Starting scan upload - ALL PHOTOS', {
        scanId: newScanId,
        hasFront: !!slotState.front.processed,
        hasLeft: !!leftSidePhoto,
        hasRight: !!rightSidePhoto,
        totalPhotos: uploadAssets.length,
        frontPhotoHash: slotState.front.processed.metadata.hash,
        leftPhotoHash: leftSidePhoto?.metadata.hash,
        rightPhotoHash: rightSidePhoto?.metadata.hash,
      });
      
      // Perform pre-scan quality checks
      const qualityCheck = performPreScanQualityCheck(
        slotState.front.processed.metadata,
        leftSidePhoto?.metadata,
        rightSidePhoto?.metadata
      );

      if (!qualityCheck.valid) {
        // Block analysis if quality checks fail
        showToast(qualityCheck.errors.join(' '), 'error');
        setSlotState((prev) => ({
          ...prev,
          front: { ...prev.front, error: qualityCheck.errors.join(' ') },
        }));
        return;
      }

      // Show warnings if any
      if (qualityCheck.warnings.length > 0) {
        qualityCheck.warnings.forEach((warning) => {
          showToast(warning, 'info');
        });
      }

      // Validate photos with AWS Rekognition - check for human faces
      // NOTE: Rekognition face validation works for ALL subscription tiers (Plus, Pro, Elite)
      // The tier-specific detection logic (what skin conditions are detected) is handled in generateScanAnalysis()
      showToast('Validating photos with AI face detection...', 'info');
      const sidePhotoForValidation = leftSidePhoto || rightSidePhoto;
      if (!sidePhotoForValidation) {
        throw new Error('Side photo is required for validation');
      }

      const rekognitionValidation = await validateScanPhotos(
        session,
        slotState.front.processed.file,
        sidePhotoForValidation.file
      );

      if (!rekognitionValidation.isValid) {
        // Block analysis if Rekognition validation fails
        const errorMessage = rekognitionValidation.errors.join(' ');
        showToast(errorMessage, 'error');
        setSlotState((prev) => ({
          ...prev,
          front: { ...prev.front, error: rekognitionValidation.front.error || 'Face validation failed' },
          side: { ...prev.side, error: rekognitionValidation.side.error || 'Face validation failed' },
        }));
        // Reset upload status so button shows "Retake" instead of "Uploading..."
        setStatus('idle');
        setIsContinuing(false);
        // Reset upload progress
        setSlotState((prev) => ({
          ...prev,
          front: { ...prev.front, uploadProgress: null },
          side: { ...prev.side, uploadProgress: null },
        }));
        return;
      }

      // Log Rekognition metadata for analysis
      console.log('[DEBUG] Rekognition validation successful', {
        frontFaceCount: rekognitionValidation.front.faceCount,
        sideFaceCount: rekognitionValidation.side.faceCount,
        frontMetadata: rekognitionValidation.front.faces[0],
        sideMetadata: rekognitionValidation.side.faces[0],
      });

      // Calculate glow score using centralized calculator
      // This is 100% deterministic - same images = same score
      const glowScoreResult = calculateGlowScore({
        frontMeta: slotState.front.processed.metadata,
        frontWarnings: slotState.front.warnings,
        leftSideMeta: leftSidePhoto?.metadata,
        leftSideWarnings: leftSidePhoto?.warnings || [],
        rightSideMeta: rightSidePhoto?.metadata,
        rightSideWarnings: rightSidePhoto?.warnings || [],
      });

      const glowScoreValue = glowScoreResult.score;
      
      console.log('[DEBUG] Glow score calculated from all photos', {
        glowScore: glowScoreValue,
        photosUsed: {
          front: true,
          left: !!leftSidePhoto,
          right: !!rightSidePhoto,
        },
      });

      const runYolo = async () => {
        try {
          return await detectAcne(slotState.front.processed!.file, session);
        } catch (e) {
          console.warn('[DEBUG] YOLOv8 acne detection failed, continuing without it', e);
          return null;
        }
      };

      const [assets, yoloAcne] = await Promise.all([
        uploadScanAssets(
          session,
          uid,
          newScanId,
          uploadAssets,
          (event) => {
            if (event.slot === 'front' || event.slot === 'side') {
              const slotKey: Slot = event.slot;
              setSlotState((prev) => ({
                ...prev,
                [slotKey]: {
                  ...prev[slotKey],
                  uploadProgress: event.progress,
                },
              }));
            }
          }
        ),
        runYolo(),
      ]);

      if (yoloAcne?.face_detected) {
        console.log('[DEBUG] YOLOv8 acne detection', {
          total: yoloAcne.total_detections,
          zone_counts: yoloAcne.zone_counts,
        });
      }

      // Determine which side photo to use for legacy 'side' field (for backward compatibility)
      const primarySidePhoto = leftSidePhoto || rightSidePhoto;
      const primarySideAngle = leftSidePhoto ? 'left' : (rightSidePhoto ? 'right' : 'unspecified');

      const baseRecord = await saveScanRecord(session, {
        uid,
        scanId: newScanId,
        status: 'processing',
        glowScore: glowScoreValue,
        customName: customScanName.trim() || undefined, // Save custom name if provided
        storageKeys: {
          front: assets.front.key,
          frontThumb: assets.front.thumbKey,
          side: assets.side.key, // Legacy - primary side photo
          sideThumb: assets.side.thumbKey, // Legacy
          ...(assets.leftSide ? {
            leftSide: assets.leftSide.key,
            leftSideThumb: assets.leftSide.thumbKey,
          } : {}),
          ...(assets.rightSide ? {
            rightSide: assets.rightSide.key,
            rightSideThumb: assets.rightSide.thumbKey,
          } : {}),
        },
        front: {
          url: assets.front.url,
          thumbUrl: assets.front.thumbUrl,
          metadata: assets.front.metadata,
        },
        side: {
          url: assets.side.url,
          thumbUrl: assets.side.thumbUrl,
          metadata: assets.side.metadata,
          angle: primarySideAngle,
        },
        ...(assets.leftSide ? {
          leftSide: {
            url: assets.leftSide.url,
            thumbUrl: assets.leftSide.thumbUrl,
            metadata: assets.leftSide.metadata,
          },
        } : {}),
        ...(assets.rightSide ? {
          rightSide: {
            url: assets.rightSide.url,
            thumbUrl: assets.rightSide.thumbUrl,
            metadata: assets.rightSide.metadata,
          },
        } : {}),
        angles: {
          front: true,
          side: primarySideAngle,
          ...(assets.leftSide ? { leftSide: true } : {}),
          ...(assets.rightSide ? { rightSide: true } : {}),
        },
        hashes: {
          front: slotState.front.processed!.metadata.hash,
          side: primarySidePhoto!.metadata.hash, // Legacy
          ...(leftSidePhoto ? { leftSide: leftSidePhoto.metadata.hash } : {}),
          ...(rightSidePhoto ? { rightSide: rightSidePhoto.metadata.hash } : {}),
        },
      });

      // Get previous scan for comparison
      let previousScan: ScanRecord | null = null;
      try {
        const history = await listUserScans(session, uid, 2);
        previousScan = history.items
          .filter((item) => item.scanId !== newScanId && item.status === 'complete')
          .sort(
            (a, b) =>
              new Date(b.createdAt ?? b.timestamp).getTime() -
              new Date(a.createdAt ?? a.timestamp).getTime()
          )[0] || null;
        console.log('[DEBUG] Previous scan for comparison', {
          found: !!previousScan,
          previousScanId: previousScan?.scanId,
          previousScore: previousScan?.glowScore ?? previousScan?.analysis?.skinHealthIndex,
        });
      } catch (historyError) {
        console.warn('Could not fetch previous scan for analysis comparison', historyError);
      }

      // Check same conditions as previous scan (after previousScan is fetched)
      if (previousScan) {
        const sameConditionsCheck = checkSameConditions(
          {
            front: slotState.front.processed.metadata,
            leftSide: leftSidePhoto?.metadata,
            rightSide: rightSidePhoto?.metadata,
          },
          previousScan
        );

        if (sameConditionsCheck.shouldWarn && sameConditionsCheck.message) {
          showToast(sameConditionsCheck.message, 'info');
        }
      }

      // Generate AI analysis - use 'both' if we have both left and right, otherwise use the available side
      const analysisSideAngle = (leftSidePhoto && rightSidePhoto) 
        ? 'unspecified' // Both sides analyzed together
        : (leftSidePhoto ? 'left' : 'right');
      
      console.log('[DEBUG] Generating analysis', {
        glowScore: glowScoreValue,
        analysisSideAngle,
        hasLeft: !!leftSidePhoto,
        hasRight: !!rightSidePhoto,
        hasPreviousScan: !!previousScan,
      });
      // Priority processing for Elite users - simulate faster processing
      const isElite = userProfile?.subscriptionTier === 'elite';
      if (isElite) {
        console.log('[Priority Processing] Elite user - processing scan with priority');
        // Elite users get faster processing (simulated by reducing delay)
        // In production, this would use a priority queue system
      }

      // Extract Rekognition metadata for analysis
      const frontFaceMetadata = rekognitionValidation.front.faces[0];
      const sideFaceMetadata = rekognitionValidation.side.faces[0];

      const analysis = generateScanAnalysis(
        glowScoreValue,
        glowScoreResult.estimatedPH,
        glowScoreResult.confidenceLevel,
        glowScoreResult.confidenceScore,
        analysisSideAngle,
        previousScan,
        !!leftSidePhoto,
        !!rightSidePhoto,
        userProfile?.dietaryPreference,
        userProfile?.subscriptionTier || 'plus',
        {
          front: frontFaceMetadata,
          side: sideFaceMetadata,
        },
        yoloAcne,
      );
      
      // Update focus area if we have both sides
      if (leftSidePhoto && rightSidePhoto) {
        analysis.acneFocusArea = 'both profiles (left & right)';
      }

      console.log('[DEBUG] Analysis generated', {
        skinHealthIndex: analysis.skinHealthIndex,
        skinHealthStatus: analysis.skinHealthStatus,
        acneSeverity: analysis.acneSeverity,
        acneFocusArea: analysis.acneFocusArea,
        recommendationsCount: analysis.recommendations.length,
        progressHighlightsCount: analysis.progressHighlights.length,
        hasNextBestStep: !!analysis.nextBestStep,
      });

      // Update scan record with analysis and mark as complete
      console.log('[DEBUG] Saving analysis to database', {
        userId: baseRecord.uid,
        scanId: newScanId,
        timestamp: baseRecord.timestamp,
        sideAngle,
        analysisKeys: Object.keys(analysis),
        acneFocusArea: analysis.acneFocusArea,
      });
      await updateScanRecord(session, baseRecord.uid, baseRecord.timestamp, {
        status: 'complete',
        analysis,
      });
      console.log('[DEBUG] Analysis saved successfully');

      // Small delay to ensure DynamoDB consistency (eventual consistency)
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verify the scan was saved with analysis by querying it back
      try {
        const verification = await listUserScans(session, uid, 5);
        const foundScan = verification.items.find(item => item.scanId === newScanId);
        if (foundScan) {
          console.log('[DEBUG] Scan verified in database with analysis', {
            userId: uid,
            scanId: newScanId,
            status: foundScan.status,
            hasAnalysis: !!foundScan.analysis,
            acneFocusArea: foundScan.analysis?.acneFocusArea,
            sideAngle: foundScan.side?.angle ?? foundScan.angles?.side,
            savedSideAngle: sideAngle,
            sideAngleMatch: (foundScan.side?.angle ?? foundScan.angles?.side) === sideAngle,
          });
          
          if (!foundScan.analysis) {
            console.warn('[DEBUG] WARNING: Scan saved but analysis field is missing!', {
              userId: uid,
              scanId: newScanId,
              timestamp: baseRecord.timestamp,
            });
          }
        } else {
          console.warn('[DEBUG] Scan not found immediately after save (eventual consistency)', {
            userId: uid,
            scanId: newScanId,
            allScanIds: verification.items.map(item => item.scanId),
          });
        }
      } catch (verifyError) {
        console.warn('[DEBUG] Could not verify scan immediately', verifyError);
      }

      const completedRecord: ScanRecord = {
        ...baseRecord,
        status: 'complete',
        updatedAt: new Date().toISOString(),
        glowScore: glowScoreValue,
        analysis,
        customName: customScanName.trim() || undefined, // Ensure custom name is saved
      };

      setScanRecord(completedRecord);
      setUploadedAssets(assets);
      setStatus('complete');
      persistHistory(completedRecord);
      rememberLastScanMeta({ scanId: newScanId, timestamp: completedRecord.timestamp });
      if (updateUserProfile) {
        const newTotalScans = (userProfile?.totalScans ?? 0) + 1;
        
        // Extract skin type and acne type from analysis
        let detectedSkinType: 'oily' | 'dry' | 'combination' | 'sensitive' | 'normal' | undefined = undefined;
        let detectedAcneType: 'mild' | 'moderate' | 'severe' | 'hormonal' | 'inflammatory' | 'post-acne' | undefined = undefined;

        if (analysis) {
          // Map acne severity to acne type (support both old and new severity values)
          const acneSeverity = analysis.acneSeverity;
          if (acneSeverity === 'mild' || acneSeverity === 'very_mild' || acneSeverity === 'none') {
            detectedAcneType = 'mild';
          } else if (acneSeverity === 'moderate' || acneSeverity === 'mild_to_moderate') {
            detectedAcneType = 'moderate';
          } else if (acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
            detectedAcneType = 'severe';
          }

          // Estimate skin type from pH and texture notes
          const estimatedPH = analysis.estimatedPH;
          const textureNotes = analysis.textureNotes?.toLowerCase() || '';
          
          if (typeof estimatedPH === 'number') {
            if (estimatedPH < 4.7 || textureNotes.includes('sensitive')) {
              detectedSkinType = 'sensitive';
            } else if (estimatedPH > 5.9 || textureNotes.includes('dry')) {
              detectedSkinType = 'dry';
            } else if (textureNotes.includes('oily') || textureNotes.includes('t-zone')) {
              detectedSkinType = 'combination';
            } else if (estimatedPH >= 4.7 && estimatedPH <= 5.9) {
              detectedSkinType = 'normal';
            }
          } else {
            // Fallback to texture notes only if pH is not available
            if (textureNotes.includes('sensitive')) {
              detectedSkinType = 'sensitive';
            } else if (textureNotes.includes('dry')) {
              detectedSkinType = 'dry';
            } else if (textureNotes.includes('oily') || textureNotes.includes('t-zone')) {
              detectedSkinType = 'combination';
            }
          }
        }

        // Update streak using Snapchat-style logic
        const currentStreakData = userProfile && userProfile.lastStreakDate ? {
          streakCount: userProfile.streakCount ?? userProfile.streak ?? 0,
          lastStreakDate: userProfile.lastStreakDate,
        } : undefined;
        
        const updatedStreak = updateStreakOnScan(currentStreakData);
        
        const profileUpdates: any = {
          glowScore: glowScoreValue,
          totalScans: newTotalScans,
          streakCount: updatedStreak.streakCount,
          lastStreakDate: updatedStreak.lastStreakDate,
          streak: updatedStreak.streakCount, // Update legacy field for backward compatibility
        };

        // Only update skin type if not already set or if we detected a more specific type
        if (detectedSkinType && (!userProfile?.skinType || userProfile.skinType === 'normal')) {
          profileUpdates.skinType = detectedSkinType;
        }

        // Only update acne type if not already set
        if (detectedAcneType && !userProfile?.acneType) {
          profileUpdates.acneType = detectedAcneType;
        }

        try {
          await updateUserProfile(profileUpdates);
          console.log('[DEBUG] Profile updated with scan data', {
            glowScore: glowScoreValue,
            totalScans: newTotalScans,
            streakCount: updatedStreak.streakCount,
            lastStreakDate: updatedStreak.lastStreakDate,
            detectedSkinType,
            detectedAcneType,
          });
        } catch (profileErr) {
          console.warn('Failed to update user profile after scan:', profileErr);
        }
      }
      dispatchScanReady(newScanId);
      
      // Show priority processing message for Elite users
      if (isElite) {
        showToast('⚡ Priority Processing: Your scan is being analyzed with priority!', 'success');
      } else {
        showToast('Photos uploaded. We\'ll start analyzing right away.', 'success');
      }
      
      navigate(`/results/${newScanId}`, {
        state: { scanTimestamp: completedRecord.timestamp },
      });
    } catch (error: any) {
      console.error('Upload flow error', error);
      setStatus('error');
      const message =
        error?.message ??
        'Something went wrong while uploading your photos. Please try again in a moment.';
      setGlobalError(message);
      showToast(message, 'error');
    } finally {
      setIsContinuing(false);
      if (forceNewScan) {
        setForceNewScan(false);
      }
    }
  };

  const confirmDuplicateReuse = () => {
    if (!duplicateCandidate) return;
    showToast('Re-using your recent upload.', 'info');
    dispatchScanReady(duplicateCandidate.scanId);
    if (duplicateCandidate.timestamp) {
      rememberLastScanMeta({ scanId: duplicateCandidate.scanId, timestamp: duplicateCandidate.timestamp });
    }
    navigate(`/results/${duplicateCandidate.scanId}`, {
      state: {
        scanTimestamp: duplicateCandidate.timestamp,
        reused: true,
      },
    });
    setDuplicateCandidate(null);
    setShowDuplicateModal(false);
    setForceNewScan(false);
  };

  const continueAfterDuplicate = () => {
    setForceNewScan(true);
    setShowDuplicateModal(false);
    setDuplicateCandidate(null);
    setScanId(null); // Clear any existing scanId to force new scan creation
    void handleContinue(true);
  };

  const beginDelete = () => {
    setShowDeleteModal(true);
  };

  const finalizeDeletion = async () => {
    if (!scanRecord || !scanId) return;
    try {
      const session = await getCurrentSession();
      if (!session) {
        throw new Error('Session expired.');
      }
      setStatus('deleting');
      await updateScanRecord(session, scanRecord.uid, scanRecord.timestamp, {
        status: 'deleting',
        deleteAt: new Date().toISOString(),
      });
      setShowDeleteSnackbar(true);
      setDeleteCountdown(10);
      const timer = setInterval(() => {
        setDeleteCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
          }
          return prev - 1;
        });
      }, 1000);
      setDeleteTimer(
        setTimeout(async () => {
          try {
            if (uploadedAssets) {
              await deleteScanAssets(session, Object.values(uploadedAssets));
            }
            await deleteScanRecord(session, scanRecord.uid, scanRecord.timestamp);
            history.remove(scanRecord.scanId);
            showToast('Scan deleted.', 'success');
            setScanRecord(null);
            setUploadedAssets(null);
            setStatus('idle');
          } catch (err) {
            console.error('Delete finalizer error', err);
            showToast('Could not finish deleting the scan. Try again later.', 'error');
            setStatus('error');
          } finally {
            setShowDeleteSnackbar(false);
          }
        }, 10000)
      );
    } catch (error) {
      console.error('Delete error', error);
      showToast('Unable to delete scan right now.', 'error');
      setStatus('error');
      setShowDeleteSnackbar(false);
    } finally {
      setShowDeleteModal(false);
    }
  };

  const undoDelete = async () => {
    if (!scanRecord) return;
    if (deleteTimer) {
      clearTimeout(deleteTimer);
      setDeleteTimer(null);
    }
    setShowDeleteSnackbar(false);
    try {
      const session = await getCurrentSession();
      if (!session) {
        throw new Error('Session expired.');
      }
      await updateScanRecord(session, scanRecord.uid, scanRecord.timestamp, {
        status: 'complete',
        deleteAt: null,
      });
      setStatus('complete');
      showToast('Deletion undone.', 'success');
    } catch (error) {
      console.error('Undo delete error', error);
      showToast('Could not undo deletion. Please re-upload if needed.', 'error');
    }
  };

  useEffect(() => {
    if (!showDeleteSnackbar) return;
    if (deleteCountdown <= 0) {
      setShowDeleteSnackbar(false);
    }
  }, [deleteCountdown, showDeleteSnackbar]);

  return (
    <UploadPage>
      <Content>
        <Header>
          <Title>Upload Photos</Title>
          <Subtitle>
            Upload two clear selfies — one straight-on and one from the side. We'll analyze lighting,
            clarity, and key skin markers to personalize your routine.
          </Subtitle>
        </Header>

        {/* Prescription Medications Section - All Plans */}
        <PrescriptionSection>
          <PrescriptionForm />
        </PrescriptionSection>

        <GuidanceCard aria-label="Photo capture guidance">
          <div>
            <GuidanceList>
              <GuidanceItem>
                <CheckCircle size={18} />
                Use a well-lit room or natural daylight; avoid harsh backlighting.
              </GuidanceItem>
              <GuidanceItem>
                <CheckCircle size={18} />
                Remove glasses, hats, and makeup. Pull hair away from your face.
              </GuidanceItem>
              <GuidanceItem>
                <CheckCircle size={18} />
                Keep the camera at eye level and center your face in frame.
              </GuidanceItem>
              <GuidanceItem>
                <CheckCircle size={18} />
                For the side view, choose either left or right — we just need one profile angle.
              </GuidanceItem>
            </GuidanceList>
          </div>
          <TipsCard>
            <strong>Tips for best results:</strong>
            <ul style={{ margin: '0.75rem 0 0', paddingLeft: '1rem' }}>
              <li>Hold the camera about an arm’s length away.</li>
              <li>Keep your expression neutral and relax your jaw.</li>
              <li>If the app flags low light or multiple faces, retake for the clearest analysis.</li>
            </ul>
          </TipsCard>
        </GuidanceCard>

        {globalError && (
          <ErrorBanner role="alert">
            <AlertCircle size={20} />
            <span>{globalError}</span>
          </ErrorBanner>
        )}

        {status === 'complete' && scanRecord && (
          <SuccessBanner>
            <CheckCircle size={22} />
            <div>
              <strong>Scan ready.</strong> You can revisit these photos or delete them anytime in Settings.
            </div>
          </SuccessBanner>
        )}

        <UploadGrid>
          {(['front', 'side'] as Slot[]).map((slot) => (
            <UploadCard key={slot}>
              <SlotHeading>
                <div>
                  <SlotTitle>{slot === 'front' ? 'Front View' : 'Side View'}</SlotTitle>
                  <SlotSubtitle>
                    {slot === 'front'
                      ? 'Face the camera straight on'
                      : 'Choose left or right profile — we’ll remember your preference'}
                  </SlotSubtitle>
                </div>
              </SlotHeading>

              {slot === 'side' && (
                <SideSelector
                  value={sideAngle}
                  onChange={(newAngle) => {
                    // When switching sides, show the photo for that side (if it exists)
                    // Don't clear photos - keep both left and right photos separate
                    console.log('[DEBUG] Switching side angle', {
                      from: sideAngle,
                      to: newAngle,
                      hasLeftPhoto: !!leftSidePhoto,
                      hasRightPhoto: !!rightSidePhoto,
                    });
                    setSideAngle(newAngle);
                    // The useEffect will handle showing the correct photo
                  }}
                  disabled={status === 'uploading' || status === 'deleting'}
                />
              )}

              {!slotState[slot].processed ? (
                <PhotoDropzone
                  key={`${slot}-${slotState[slot].autoOpen ? 'open' : 'closed'}`}
                  title={slot === 'front' ? 'Upload front view' : 'Upload side view'}
                  description="Drag & drop your photo or choose a file"
                  onFileSelected={(file) => handleFileSelection(slot, file)}
                  onError={(message) =>
                    setSlotState((prev) => ({
                      ...prev,
                      [slot]: {
                        ...prev[slot],
                        error: message,
                      },
                    }))
                  }
                  autoOpen={slotState[slot].autoOpen}
                  disabled={status === 'uploading' || status === 'deleting'}
                />
              ) : (
                <PhotoPreview
                  title={slot === 'front' ? 'Front view' : 'Side view'}
                  imageSrc={slotState[slot].processed!.dataUrl}
                  metadata={slotState[slot].processed!.metadata}
                  warnings={slotState[slot].warnings}
                  infos={slotState[slot].infos}
                  onReplace={() => handleReplace(slot)}
                  onCrop={() => handleOpenCrop(slot)}
                  disabled={status === 'uploading' || status === 'deleting'}
                  progress={slotState[slot].uploadProgress}
                />
              )}

              {slotState[slot].error && (
                <ErrorBanner role="alert">
                  <AlertCircle size={20} />
                  <span>{slotState[slot].error}</span>
                </ErrorBanner>
              )}
            </UploadCard>
          ))}
        </UploadGrid>

        <PrivacyNotice>
          <Info size={20} />
          <div>
            Your photos are used to analyze skin characteristics. We don't sell your images, and you can delete
            them anytime in Settings. Metadata is stripped before upload for privacy.
          </div>
        </PrivacyNotice>

        {bothPhotosReady && (
          <ScanNameInput>
            <ScanNameLabel>
              <span>Name this scan (optional)</span>
              <span style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, fontWeight: 'normal' }}>
                Give your scan a memorable name to easily identify it later
              </span>
            </ScanNameLabel>
            <ScanNameField
              type="text"
              placeholder="e.g., Morning Routine Check, After Treatment, Week 1 Progress..."
              value={customScanName}
              onChange={(e) => setCustomScanName(e.target.value)}
              maxLength={50}
              disabled={status === 'uploading' || status === 'deleting'}
            />
          </ScanNameInput>
        )}

        <ActionsBar>
          <Button
            onClick={() => {
              if (slotState.front.error || slotState.side.error) {
                handleRetake();
              } else {
                handleContinue();
              }
            }}
            disabled={status === 'uploading' || status === 'deleting'}
            size="lg"
            loading={isContinuing && !(slotState.front.error || slotState.side.error)}
          >
            {status === 'uploading' ? 'Uploading…' : 
             (slotState.front.error || slotState.side.error) ? 'Retake' : 'Continue'}
          </Button>
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </ActionsBar>

        {(status === 'complete' || status === 'deleting') && scanRecord && (
          <DeleteSection>
            <DeleteCopy>
              <h4>Delete this scan?</h4>
              <p>You can permanently remove the photos and data for this scan if you no longer need it.</p>
            </DeleteCopy>
            <Button variant="destructive" onClick={beginDelete} disabled={status !== 'complete'}>
              <Trash2 size={18} style={{ marginRight: '0.5rem' }} />
              Delete scan
            </Button>
          </DeleteSection>
        )}
      </Content>

      {cropState.open && (
        <ModalOverlay aria-modal="true" role="dialog">
          <ModalCard>
            <ModalTitle>Adjust {cropState.slot === 'front' ? 'front' : 'side'} photo</ModalTitle>
            <CropContainer>
              <Cropper
                image={cropState.imageSrc}
                crop={cropState.crop}
                zoom={cropState.zoom}
                rotation={cropState.rotation}
                aspect={3 / 4}
                onCropChange={(crop) => setCropState((prev) => ({ ...prev, crop }))}
                onZoomChange={(zoom) => setCropState((prev) => ({ ...prev, zoom }))}
                onRotationChange={(rotation) => setCropState((prev) => ({ ...prev, rotation }))}
                onCropComplete={handleCropComplete}
              />
            </CropContainer>
            <CropControls>
              <label>
                Zoom
                <Slider
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={cropState.zoom}
                  onChange={(event) =>
                    setCropState((prev) => ({
                      ...prev,
                      zoom: Number(event.target.value),
                    }))
                  }
                />
              </label>
              <label>
                Rotation
                <Slider
                  type="range"
                  min={-45}
                  max={45}
                  step={1}
                  value={cropState.rotation}
                  onChange={(event) =>
                    setCropState((prev) => ({
                      ...prev,
                      rotation: Number(event.target.value),
                    }))
                  }
                />
              </label>
            </CropControls>
            <ModalActions>
              <Button variant="ghost" onClick={() => setCropState((prev) => ({ ...prev, open: false }))}>
                Cancel
              </Button>
              <Button onClick={commitCrop}>Apply crop</Button>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {showDuplicateModal && duplicateCandidate && (
        <ModalOverlay aria-modal="true" role="dialog">
          <ModalCard>
            <ModalTitle>Looks like you already uploaded these photos</ModalTitle>
            <p>
              We found a scan from {new Date(duplicateCandidate.timestamp).toLocaleString()} that matches both
              images. Would you like to reuse that scan to save time?
            </p>
            <ModalActions>
              <Button variant="ghost" onClick={continueAfterDuplicate}>
                Upload anyway
              </Button>
              <Button onClick={confirmDuplicateReuse}>Use existing scan</Button>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {showDeleteModal && (
        <ModalOverlay aria-modal="true" role="dialog">
          <ModalCard>
            <ModalTitle>Delete this scan?</ModalTitle>
            <p>
              This will remove your photos and data for this scan. You'll have 10 seconds to undo after you
              delete. This action cannot be undone later.
            </p>
            <ModalActions>
              <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={finalizeDeletion}>
                Delete scan
              </Button>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Scan Limit Reached Modal */}
      {showLimitReachedModal && (
        <ModalOverlay onClick={() => setShowLimitReachedModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Weekly Scan Limit Reached</ModalTitle>
            <p style={{ margin: 0, color: theme.colors.text.secondary, lineHeight: 1.6 }}>
              You've reached your weekly scan limit for your current plan.
              {scansUsed !== undefined && scansAllowed !== undefined && scansAllowed !== 'unlimited' && (
                <span style={{ display: 'block', marginTop: '0.5rem', fontSize: theme.typography.fontSize.sm }}>
                  You've used {scansUsed} of {scansAllowed} scans this week.
                </span>
              )}
            </p>
            <ModalActions>
              <Button
                variant="outline"
                onClick={() => setShowLimitReachedModal(false)}
              >
                Maybe Later
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowLimitReachedModal(false);
                  navigate('/pricing');
                }}
              >
                Upgrade
              </Button>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {showDeleteSnackbar && (
        <Snackbar role="status">
          <span>Scan deleted. Undo? ({deleteCountdown}s)</span>
          <SnackbarButton onClick={undoDelete}>Undo</SnackbarButton>
        </Snackbar>
      )}
    </UploadPage>
  );
};

export default ScanPage;
